import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {getAdministrationCredentialsToConfigure, StoreState} from '@redux/reducers';
import {combineLatest, Observable, of} from 'rxjs';
import {Profile} from '@model/profile';
import {Router} from '@angular/router';
import {AccessRight} from '@model/access-right';
import {catchError, filter, map, switchMap, take, tap} from 'rxjs/operators';
import {School} from '@model/school';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {AgendaActivity} from '@model/agenda-activity';
import {SchoolService} from '@services/school.service';
import {AdministrationCredentialsActions, PublicMessageSavedAction} from '@redux/actions/school.action';
import {getAdministrationCredentialsByEstablishment} from '@redux/reducers';
import {PublicMessage} from '@model/public-message';
import {AuthenticationService} from '@services/authentication.service';
import {SchoolSetting} from '@model/school-setting';
import {ParentMeetingSlot} from '@model/parent-meeting-slot';

@Injectable({
  providedIn: 'root'
})
export class AppLayoutFacadeService {

  school$: Observable<School>;
  profiles$: Observable<Profile[]>;
  isSuperAdmin: boolean;
  schoolName$: Observable<string>;
  firstName$: Observable<string>;
  lastName$: Observable<string>;
  isAdminStaffMember$: Observable<boolean>;
  isEducatorStaffMember$: Observable<boolean>;
  isTeacherStaffMember$: Observable<boolean>;
  isStaffMember$: Observable<boolean>;
  isParent$: Observable<boolean>;
  isStudent$: Observable<boolean>;
  hasAbsenceManagementRole$: Observable<boolean>;
  hasResourceManagementRole$: Observable<boolean>;
  hasIlpManagementRole$: Observable<boolean>;
  hasDisciplinaryManagementRole$: Observable<boolean>;
  hasStudentManagementRole$: Observable<boolean>;
  selectedProfile$: Observable<Profile>;
  isMobile$: Observable<boolean>;
  agendaActivities$: Observable<AgendaActivity[]>;
  publicMessage$: Observable<PublicMessage>;
  schoolSettings$: Observable<SchoolSetting[]>;
  parentMeetingSlots$: Observable<ParentMeetingSlot[]>;
  schoolSynchronizationEnabled$: Observable<boolean>;
  administrationAttendanceAPIIntegrationEnabled$: Observable<boolean>;
  administrationCredentialsToConfigure$: Observable<boolean>;
  email: string;
  cabangaLite$: Observable<boolean>;

  constructor(private _store: Store<StoreState>,
              private _router: Router,
              private _sharedFacade: SharedFacadeService,
              private _authenticationService: AuthenticationService,
              private _schoolService: SchoolService) {

    this.school$ = this._sharedFacade.school$;
    this.profiles$ = this._store.pipe(
      select(state => state.profile.profiles)
    );
    this.selectedProfile$ = this._store.pipe(
      select(state => state.profile.selectedProfile),
      filter(profile => !!profile.id)
    );
    this.schoolName$ = this._store.pipe(
      select(state => state.profile.selectedProfile.schoolName)
    );
    this.firstName$ = this._store.pipe(
      select(state => state.profile.selectedProfile.firstName)
    );
    this.lastName$ = this._store.pipe(
      select(state => state.profile.selectedProfile.lastName)
    );
    this.isAdminStaffMember$ = this._sharedFacade.isAdminStaffMember$;
    this.isEducatorStaffMember$ = this._sharedFacade.isEducatorStaffMember$;
    this.isTeacherStaffMember$ = this._sharedFacade.isTeacherStaffMember$;
    this.isStaffMember$ = this._sharedFacade.isStaffMember$;
    this.isParent$ = this._sharedFacade.isParent$;
    this.isStudent$ = this._sharedFacade.isStudent$;
    this.isSuperAdmin = this._authenticationService.isSuperAdmin();
    this.hasAbsenceManagementRole$ = this._sharedFacade.hasAbsenceManagementRole$;
    this.hasResourceManagementRole$ = this._sharedFacade.hasResourceManagementRole$;
    this.hasIlpManagementRole$ = this._sharedFacade.hasIlpManagementRole$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.agendaActivities$ = this._sharedFacade.agendaActivities$;
    this.publicMessage$ = this._sharedFacade.publicMessage$;
    this.hasDisciplinaryManagementRole$ = this._sharedFacade.hasDisciplinaryManagementRole$;
    this.hasStudentManagementRole$ = this._sharedFacade.hasStudentManagementRole$;
    this.schoolSettings$ = this._sharedFacade.schoolSettings$;
    this.parentMeetingSlots$ = this._sharedFacade.parentMeetingSlots$;
    this.email = this._authenticationService.email;
    this.schoolSynchronizationEnabled$ = this.school$.pipe(
      map(school => school.studentHorizonSynchronizationEnabled ?? false)
    );
    this.administrationAttendanceAPIIntegrationEnabled$ = this.school$.pipe(
      map(school => school.administrationAttendanceAPIIntegrationEnabled ?? false)
    )
    this.cabangaLite$ = this.school$.pipe(
      map(school => !!school.cabangaLite)
    );
    this.administrationCredentialsToConfigure$ = this._store.pipe(
      select(getAdministrationCredentialsToConfigure)
    );

    this.loadAdministrationCredentialsIfAdminAndApiIntegrationEnabled();
  }

  private loadAdministrationCredentialsIfAdminAndApiIntegrationEnabled() {
    combineLatest([this.school$, this.isAdminStaffMember$]).pipe(
      filter(([school, isAdmin]) => !!school?.id && !!school.administrationAttendanceAPIIntegrationEnabled && isAdmin),
      take(1),
    ).subscribe(() => this._sharedFacade.loadAdministrationCredentials());
  }

  changeProfile() {
    this._router.navigate(['profiles']);
  }

  logout() {
    this._authenticationService.logout();
  }

  goToHelp() {
    this.cabangaLite$.pipe(
      take(1)
    ).subscribe(cabangaLite => {
      if (cabangaLite) {
        window.open('https://scolares.odoo.com/knowledge/article/2058', '_blank');
      } else {
        window.open('https://scolares.odoo.com/knowledge/article/38', '_blank');
      }
    });
  }

  goToAbout() {
    this._router.navigate(['app/about']);
  }

  goToOffice365() {
    window.open('https://portal.office365.com', '_blank');
  }

  goToGoogleApps(app: string) {
    switch (app) {
      case 'drive':
        window.open('https://drive.google.com/drive/my-drive', '_blank');
        break;
      case 'gmail':
        window.open('https://mail.google.com/mail/u/0/', '_blank');
        break;
      case 'classroom':
        window.open('https://classroom.google.com/', '_blank');
        break;
    }
  }

  profileStaffMemberHasRight(accessRights: AccessRight[]): Observable<boolean> {
    return this._sharedFacade.profileStaffMemberHasRight(accessRights);
  }

  loadAgendaActivities() {
    this._sharedFacade.loadAgendaActivities();
  }

  loadPublicMessage() {
    this._sharedFacade.loadPublicMessage();
  }

  savePublicMessage(publicMessage: PublicMessage) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.savePublicMessage(school.id, publicMessage))
    ).subscribe(() => this._store.dispatch(new PublicMessageSavedAction(publicMessage)));
  }

  loadSchoolSettings() {
    this._sharedFacade.loadSchoolSettings();
  }

  loadParentMeetingSlots() {
    this._sharedFacade.loadParentMeetingSlots();
  }

  parentMeetingSlotsReset() {
    this._sharedFacade.resetParentMeetingSlots();
  }
}
